import React, { useState, useEffect, useCallback } from "react";
import debounce from 'lodash/debounce';
import { decode } from 'he';
import ReactDOM from "react-dom";

// Render the react block SearchResults on the div with id search-results-block__render-results
document.addEventListener("DOMContentLoaded", () => {
    const divToUpdate = document.querySelector("#articles-list__search-container");
    if(divToUpdate) { 
        const SearchResults = () => {
            const [allResults, setAllResults] = useState([]);
            const [filteredResults, setFilteredResults] = useState([]);
            const [searchTerm, setSearchTerm] = useState("");
            const [resultsPerPage, setResultsPerPage] = useState(15);
            const [currentPage, setCurrentPage] = useState(1);
            const [loading, setLoading] = useState(false);
            const initialResults = document.querySelector("#articles-list__initial-data");
            const maxPages = Math.ceil(filteredResults.length / resultsPerPage);
            const url = new URL(window.location.href);
            const category = document.querySelector(".articles-list__header").dataset.category;
            const language = document.querySelector(".articles-list__header").dataset.language;

            // Render the paginated results

            // Run an initial search based on the current category and populate allResults and filteredResults
            useEffect(() => {
              setLoading(true);
              if (category) {
                const initialSearchURL = url.origin + "/wp-json/ifpbm/v1/resources?category=" + category + "&language=" + language + "&results_per_page=30";
                const allResultsURL = url.origin + "/wp-json/ifpbm/v1/resources?language=" + language + "&category=" + category;

                fetch(initialSearchURL)
                  .then((response) => response.json())
                  .then((data) => {
                    setAllResults(data);
                    setFilteredResults(data);
                    setLoading(false);
                    hideInitialResults();
                  })
                  .catch((error) => {
                    console.error(error);
                    setLoading(false);
                  });

                fetch(allResultsURL)
                  .then((response) => response.json())
                  .then((data) => {
                    setAllResults(data);
                    setFilteredResults(data);
                  })
                  .catch((error) => {
                    console.error(error);
                  });
              }
            }, []);

            // Hide the initial results
            const hideInitialResults = () => {
                initialResults.style.display = "none";
            }

            // Fetch search the results with a delay
            const delayedSearch = useCallback(
              debounce((searchTerm) => {
                if (searchTerm) {
                  // Filter the results based on the search term
                  const filteredResults = allResults.filter(result => result.title.toLowerCase().includes(searchTerm.toLowerCase()));
                  setFilteredResults(filteredResults);
                  setLoading(false);
                } else {
                  // If no search term, display all results
                  setFilteredResults(allResults);
                  setLoading(false);
                }
              }, 500),
              [filteredResults]
            );


            // Turn on spinner, update searchTerm and run the delayedSearch function
            const updateSearch = (searchTerm) => {
                setSearchTerm(searchTerm);
                setLoading(true);
                delayedSearch(searchTerm);
            }

            // Pagination functions
            const handlePageChange = (pageNumber) => {
                if (currentPage !== pageNumber) {
                    setCurrentPage(pageNumber);
                }
            };

            const handlePrevPage = () => {
                if (currentPage > 1) {
                    setCurrentPage((prevPage) => prevPage - 1);
                }
            };

            const handleNextPage = () => {
                if (currentPage < maxPages) {
                    setCurrentPage((prevPage) => prevPage + 1);
                }
            };

            const handleFirstPage = () => {
                if (currentPage !== 1) {
                    setCurrentPage(1);
                }
            };

            const handleLastPage = () => {
                if (currentPage !== maxPages) {
                    setCurrentPage(maxPages);
                }
            };
            const startIdx = (currentPage - 1) * resultsPerPage;
            const endIdx = startIdx + resultsPerPage;
            const paginatedResults = filteredResults.slice(startIdx, endIdx);
            // End pagination functions

            return (
              <>
                  <div className="articles-list__search-section">
                    {/* Search input */}
                    <input
                      type="text"
                      placeholder="Title Filter"
                      value={searchTerm}
                      onChange={(e) => updateSearch(e.target.value)}
                    />



                    {/* Results per page */}
                    <select
                      name="options"
                      onChange={(e) => setResultsPerPage(parseInt(e.target.value))}
                      value={resultsPerPage}
                    >
                      <option value="2">2</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>

                  {/* Results Section */}
                  <div className="articles-list-main">
                    <div className="articles-list-main__top">
                      <p>Title</p>
                      <p>Date Published</p>
                    </div>

                    {/* Loading spinner */}
                    {loading && <div id="loading-spinner"></div>}

                    {/* Results list after spinner disappears */}
                    {!loading && (
                      <div className="articles-list-main__results">
                        {paginatedResults.length > 0 ? (
                          paginatedResults.map((result) => (
                            <a
                              href={result.permalink}
                              key={result.id}
                              className="articles-list-main__result"
                            >
                              <p className="articles-list-main__title">{decode(result.title)}</p>
                              <p className="articles-list-main__date">{result.date}</p>
                            </a>
                          ))
                        ) : (
                          <p className="articles-list-main__results-message">
                            No results found
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {maxPages > 1 && (
                      <div className="articles-list-main__pagination">
                        <div className="articles-list-main__pagination-back">
                          <a onClick={handleFirstPage}>FIRST</a>
                          <a onClick={handlePrevPage}>PREV</a>
                        </div>

                        <div className="articles-list-main__pagination-numbers">
                          {[...Array(maxPages)].map((_, index) => {
                            // Calculate the lower and upper bounds for the displayed pages
                            const lowerBound = currentPage - 5;
                            const upperBound = currentPage + 5;

                            // Display the page number only if it falls within the bounds
                            if (index + 1 >= lowerBound && index + 1 <= upperBound) {
                              return (
                                <a
                                  key={index}
                                  className={currentPage === index + 1 ? "active" : ""}
                                  onClick={() => handlePageChange(index + 1)}
                                >
                                  {index + 1}
                                </a>
                              );
                            }

                            return null; // Skip rendering for pages outside the bounds
                          })}
                        </div>


                        <div className="articles-list-main__pagination-forward">
                          <a onClick={handleNextPage}>NEXT</a>
                          <a onClick={handleLastPage}>LAST</a>
                        </div>
                      </div>
                  )}
              </>
            );
            }

        ReactDOM.render(<SearchResults />, divToUpdate);

    }
});
